import { makeAutoObservable, observable, runInAction, toJS } from 'mobx'
import { isEqual } from 'lodash'
import { injectable, inject, decorate } from '@evertel/di'
import { Api } from '@evertel/api'
import { APIDataAny, APIDataDocumentSchema, APIDataDocumentSchemaCategory, APIDataMedia, SchemaProps } from '@evertel/types'
import { DocumentSchemaStore } from '@evertel/document'
import { SchemaFieldItemProps, itemProps } from '../../view/schema-builder/data/_schemaItemProps'
import { SchemaMediaUtils } from '../../utils'
import { reorder } from '@evertel/utils'

// defaults
const defaultIconColor = '#359AA2'
const defaultIcon = 'file'
const defaultName = 'Unknown EverDoc'
const defaultRow  = {
    type: 'row',
    items: []
}

class SchemaBuilderController {
    id = 0
    name = ''
    description = ''
    isEnabled = false
    meta = {}
    icon = 'file'
    color = defaultIconColor
    iconMedia: APIDataMedia|null = null
    iconFile: File|null = null
    departmentId = 0
    createdDate: Date|undefined = undefined
    updatedDate: Date|undefined = undefined
    schema: SchemaProps = {} as SchemaProps
    tileSchema: { fields: string[] }|null = null
    categories: APIDataDocumentSchemaCategory[] = []
    isInitialized = false

    schemaKey = 'root'
    documentsCount = 0 // number of docs using this schema

    // Snapshot of categories at load time, used to diff on save
    private _originalCategoryIds: Set<number> = new Set()

    // Everything the Save button would send, as it was when the template was last loaded or
    // saved. Compared against the live values so the page can warn about unsaved work
    private _savedState: Record<string, unknown> = {}

    // Titles of fields deleted from the form, kept so the room tile can still name a field it
    // references after the form has lost it. _savedState would do for the current edit, but it
    // is rebuilt on save and these have to outlive that
    private _removedFieldTitles: Record<string, string> = {}

    constructor(
        private api: Api,
        private schemaStore: DocumentSchemaStore,
        private schemaMediaUtils: SchemaMediaUtils
    ) {
        // the snapshot is compared, never read through, so it only needs to be observable by
        // reference - deep-converting a copy of the whole schema on every save would be wasteful
        makeAutoObservable<SchemaBuilderController, '_savedState'>(this, { _savedState: observable.ref })
    }

    init = async (schemaId: number, departmentId: number) => {
        let docSchema: APIDataDocumentSchema
        let docsCount = 0

        if (schemaId) {
            docSchema = await this.api.Routes.DocumentSchema.getById(schemaId, {
                include: ['categories', 'iconMedia']
            })

            docsCount = (await this.api.Routes.DocumentSchema.getDocumentsCount(schemaId)).count as number

        } else {
            // default for new schema
            docSchema = {
                schema: {
                    layouts: {
                        full: [
                            defaultRow
                        ]
                    },
                    fields: {
                    },
                    internalSchemas: {}
                }
            }
        }

        runInAction(() => {
            this.id = schemaId
            this.documentsCount = docsCount
            this.departmentId = (departmentId) ? departmentId : 0
            this.schema = docSchema.schema
            this.tileSchema = docSchema.tileSchema ?? null
            this.isEnabled = docSchema.isEnabled || false
            this.meta = docSchema.meta || {}
            this.icon = docSchema.icon || defaultIcon
            this.color = docSchema.color || defaultIconColor
            this.iconMedia = docSchema.iconMedia ?? null
            this.iconFile = null
            this.name = docSchema.name || defaultName
            this.description = docSchema.description || ''
            this.createdDate = docSchema.createdDate || undefined
            this.updatedDate = docSchema.updatedDate || undefined
            this.categories = docSchema.categories || []
            this._originalCategoryIds = new Set((docSchema.categories || []).map(c => c.id as number))
            this.isInitialized = true
            this._savedState = this.currentState
        })
    }

    // A plain snapshot of everything save() sends. iconFile is not part of the payload but a
    // staged image is still an unsaved change, so it counts as one here
    private get currentState() {
        return {
            name: this.name,
            description: this.description,
            isEnabled: this.isEnabled,
            icon: this.icon,
            color: this.color,
            iconMediaId: this.iconMedia?.id ?? null,
            hasStagedIcon: !!this.iconFile,
            meta: toJS(this.meta),
            schema: toJS(this.schema),
            tileSchema: toJS(this.tileSchema),
            categoryIds: this.categories.map(c => c.id).sort()
        }
    }

    get hasUnsavedChanges() {
        if (!this.isInitialized) return false

        return !isEqual(this._savedState, this.currentState)
    }

    // The form itself is what the API diffs to decide whether to snapshot a revision, so this
    // is the narrower "saving will pin existing documents" case. A template that has not been
    // created yet has nothing to revise
    get hasFormChanges() {
        if (!this.isInitialized || !this.id) return false

        return !isEqual(this._savedState.schema, toJS(this.schema))
    }

    // Tile entries whose field the form no longer has. They are deliberately not pruned: a
    // document pinned to a revision that predates the tile falls back to this list and resolves
    // it against its own older form, so a field removed here can still be one those documents
    // show. Dropping it has to stay a deliberate act in the room preview settings
    get danglingTileFields(): { id: string, title: string }[] {
        const liveFields = this.schema?.fields || {}
        const savedFields = (this._savedState.schema as SchemaProps)?.fields || {}

        return (this.tileSchema?.fields || [])
            .filter(fieldId => !liveFields[fieldId])
            .map(fieldId => ({
                id: fieldId,
                title: this._removedFieldTitles[fieldId] || savedFields[fieldId]?.title || 'Removed field'
            }))
    }

    // Only the ones this edit is responsible for. An entry that was already dangling when the
    // template loaded is not an unsaved change and has no business in the unsaved changes notice
    get hasTileFieldsRemovedThisSession() {
        if (!this.isInitialized) return false

        const savedFields = (this._savedState.schema as SchemaProps)?.fields || {}

        return this.danglingTileFields.some(field => !!savedFields[field.id])
    }

    save = async () => {
        const schemaData = {
            departmentId: this.departmentId, // if departmentId is 0 it is a global template
            name: this.name,
            description: this.description,
            isEnabled: this.isEnabled,
            icon: this.icon,
            color: this.color,
            iconMediaId: this.iconMedia?.id ?? null,
            meta: this.meta,
            schema: this.schema,
            tileSchema: this.tileSchema
        }

        let schema: APIDataDocumentSchema|undefined = undefined
        let iconMedia: APIDataMedia|null = this.iconMedia

        if (this.id) {
            // process any media (mutates this.schema in place)
            await this.schemaMediaUtils.processSchemaMedia(this.id, this.schema)

            // process a newly picked icon image, if any (deletes the replaced one)
            iconMedia = await this.processIconMedia(this.id)
            schemaData.iconMediaId = iconMedia?.id ?? null

            // edit
            schema = await this.api.Routes.DocumentSchema.putById(this.id, schemaData)
        } else {
            // create
            if (this.departmentId) {
                // department template
                schema = await this.api.Routes.Department.postDocumentSchemas(this.departmentId, schemaData)
            } else {
                // global template
                schema = await this.api.Routes.DocumentSchema.post(schemaData)
            }

            if (schema?.id) {
                // process any media after template is created (mutates this.schema in place)
                await this.schemaMediaUtils.processSchemaMedia(schema.id, this.schema)

                // process a newly picked icon image, if any
                iconMedia = await this.processIconMedia(schema.id)
                schemaData.iconMediaId = iconMedia?.id ?? null

                // update the new schema with the new media Ids
                schema = await this.api.Routes.DocumentSchema.putById(schema.id, schemaData)
            }
        }

        runInAction(() => {
            this.iconMedia = iconMedia
            this.iconFile = null
        })

        // process category changes
        if (schema?.id) {
            const currentCategoryIds = new Set(this.categories.map(c => c.id as number))

            // Remove categories that were in the original set but no longer present
            for (const id of this._originalCategoryIds) {
                if (!currentCategoryIds.has(id)) {
                    try {
                        await this.api.Routes.DocumentSchema.delCategoriesById(schema.id, id)
                    } catch (e) {
                        console.error('Failed to remove category', id, e)
                    }
                }
            }

            // Add categories that are new (not in the original set)
            for (const cat of this.categories) {
                if (!this._originalCategoryIds.has(cat.id as number)) {
                    try {
                        await this.api.Routes.DocumentSchema.putCategoriesById(schema.id, cat.id as number)
                    } catch (e) {
                        console.error('Failed to add category', cat.id, e)
                    }
                }
            }

            // Update the store with current categories so the drawer reflects changes.
            // Relations aren't in the PUT response, so both have to be passed explicitly
            runInAction(() => {
                this.schemaStore.update({
                    ...schema,
                    categories: this.categories,
                    iconMedia: this.iconMedia
                })
                this._originalCategoryIds = new Set(this.categories.map(c => c.id as number))
            })
        }

        // everything above is now persisted, so this is the new baseline for the unsaved warning
        runInAction(() => {
            this.id = schema?.id ?? this.id
            this._savedState = this.currentState
        })

        return schema
    }

    updateListLayoutField = (data: SchemaFieldItemProps) => {
        // this is for updating root list properties, NOT the list's fields or layout
        // NOTE: expects the full field object
        if (!data) return

        const dataKey: string = Object.keys(data)[0]

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy : schemaCopy.internalSchemas[this.schemaKey]

        // grab data and index of matching row
        const newData = data[dataKey]
        const rowIndex = schema.layouts.full.findIndex((row: any) => row.items?.some((item: any) => item.type === `&${newData?.internalSchemaId}`))

        if (rowIndex === -1) {
            // if the row does not exist, error
            console.error('ERROR: could not find row for list id "'+newData.internalSchemaId+'"')
            return
        }

        // grab row and matching index of item
        const row = schema.layouts.full[rowIndex]
        const itemIndex = row.items?.findIndex((r: any) => r.type === `&${newData?.internalSchemaId}`)

        if (itemIndex === -1) {
            // if the row does not exist, error
            console.error('ERROR: could not find list id "&'+newData.internalSchemaId+'" in row items')
            return
        }

        const currentItemData = row.items[itemIndex]

        // update item data
        row.items[itemIndex] = {...currentItemData, ...newData}

        // merge new and old data, update schema observable
        schema.layouts.full[rowIndex] = row
        this.schema = schemaCopy
    }

    updateField = (field: SchemaFieldItemProps) => {
        // NOTE: expects the full field object
        if (!field) return

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy : schemaCopy.internalSchemas[this.schemaKey]

        // grab key name of field
        const fieldKey: string = Object.keys(field)[0]

        // grab new and current field data
        const newFieldData = field[fieldKey]
        const currentFieldData = schema.fields[fieldKey]

        if (!currentFieldData) {
            // if the field does not exist, error
            console.error('ERROR: no field named '+fieldKey+' found')
            return
        }

        // merge new and old data, update schema observable
        schema.fields[fieldKey] = {...currentFieldData, ...newFieldData}
        this.schema = schemaCopy
    }

    addField = (fieldData: SchemaFieldItemProps): string => {
        // this adds a new field to the FIELDS object in the schema
        // NOTE: expects the full field object with all it's params

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy : schemaCopy.internalSchemas[this.schemaKey]

        // set new field key
        const fieldKey = `${fieldData.type}-field-${this.UID}`

        // set data to new key, update schema observable
        schema.fields[fieldKey] = { ...fieldData, id: fieldKey }
        this.schema = schemaCopy

        return fieldKey
    }

    addRow = (index: number) => {
        // this adds a new row to the schema.layouts for whichever schemaKey is active

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // insert empty row at index, update schema observable
        schema.splice(index, 0, defaultRow)
        this.schema = schemaCopy

    }

    updateRow = (oldIndex: number, newIndex: number) => {
        // this reorders rows in the schema.layouts.full array
        if (isNaN(oldIndex) || isNaN(newIndex)) {
            return
        }

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schemaFullLayout = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // reorder rows
        const newLayout = reorder(
            schemaFullLayout,
            oldIndex,
            newIndex
        )

        if (this.schemaKey === 'root') {
            schemaCopy.layouts.full = newLayout
        } else {
            schemaCopy.internalSchemas[this.schemaKey].layouts.full = newLayout
        }

        // update schema observable
        this.schema = schemaCopy
    }

    addFieldToRow = (rowIndex: number, fieldId: string) => {
        // this adds a field to a row in the schema.layouts.full array
        if (!fieldId || isNaN(rowIndex)) {
            return
        }

        // copy object (remove from observability so we can manipulate)
        let schemaCopy = toJS(this.schema)
        let schema = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // build the field props using field config props
        const field: SchemaFieldItemProps = itemProps[fieldId]
        const fieldKeys = Object.keys(field)
        const fieldData = {} as SchemaFieldItemProps

        // set props to defaults set in field config props
        fieldKeys.forEach((key) => {
            fieldData[key] = field[key].default
        })

        // add new field to FIELDS object
        const newFieldId = this.addField({
            ...fieldData,
            type: fieldId
        })

        // grab a new copy of the schema (new field was added)
        schemaCopy = toJS(this.schema)
        schema = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // add new field to target row
        const row = schema[rowIndex]
        row.items = [
            ...row.items,
            { type: `#${newFieldId}` }
        ]
        schema[rowIndex] = row

        this.schema = schemaCopy
    }

    moveFieldToRow = (oldRowIndex: number, newRowIndex: number, fieldId: string) => {
        // this moves an existing field to an existing row
        if (!fieldId || isNaN(oldRowIndex) || isNaN(newRowIndex)) {
            return
        }

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // defense for bad v1 structure
        if (!schema[newRowIndex].items) return

        // remove field from old row
        const row = schema[oldRowIndex]
        if (!row?.items) return
        const targetItem = row.items.find((i: any) => i.type === fieldId)
        const rowItems = row.items.filter((i: any) => i.type !== fieldId)
        schema[oldRowIndex] = { ...row, items: rowItems }

        // add field to new row
        schema[newRowIndex].items.push(targetItem)

        // update schema observable
        this.schema = schemaCopy
    }

    reorderFieldInRow = (fieldId: string, rowIndex: number, currentItemIndex: number, newItemIndex: number) => {
        // this reorders a field in a row (horizontally)

        if (!fieldId || isNaN(rowIndex) || isNaN(currentItemIndex) || isNaN(newItemIndex)) {
            return
        }

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // grab the row
        const row = schema[rowIndex]
        if (!row) return

        // reorder items
        const items = reorder(
            row.items,
            currentItemIndex,
            newItemIndex
        )
        // add new order back to row
        schema[rowIndex].items = items
        // update schema ovservable
        this.schema = schemaCopy

    }

    addListToRow = (rowIndex: number, fieldId: string) => {
        // this adds a LIST field to a row in the schema.layouts.full array
        if (!fieldId || isNaN(rowIndex)) {
            return
        }

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)

        const field = itemProps[fieldId]
        const fieldKeys = Object.keys(field)
        const fieldData = {} as SchemaFieldItemProps

        // construct the proper field with default values
        fieldKeys.forEach((key) => {
            fieldData[key] = field[key].default
        })

        // set list id
        const internalId = `list-${this.UID}`

        // add to internalSchemas array
        schemaCopy.internalSchemas[internalId] = {
            layouts: {
                full: [defaultRow]
            },
            fields: {},
            internalSchemas: {}
        }

        // add new field to target row
        const row = schemaCopy.layouts.full[rowIndex]
        row.items = [
            ...row.items,
            {
                ...fieldData,
                type: `&${internalId}`
            }
        ]
        schemaCopy.layouts.full[rowIndex] = row

        this.schema = schemaCopy
    }

    deleteField = (fieldId: string, rowIndex: number) => {
        // this will delete the field AND remove its reference from layouts.full
        // it also cleans up internalSchemas if the field is a list / subform

        // note: we don't allow the deletion of the 'title' field (this is just a failsafe here)
        if (!fieldId || isNaN(rowIndex)) return

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)

        // pick which schema we're working in
        // root → top-level form; otherwise → subform
        const selectedSchemaCopy = this.schemaKey === 'root' ? schemaCopy : schemaCopy.internalSchemas[this.schemaKey]

        // grab the row that contains this field
        const row = selectedSchemaCopy.layouts.full[rowIndex]
        if (!row) return

        // filter out the item we’re deleting, but capture it in the same pass
        let removedItem: any = null
        const items = row.items?.filter((i: any) => {
            const match = i?.type?.substring(1) === fieldId
            if (match) removedItem = i
            return !match
        })

        // rebuild row (minus the deleted item)
        selectedSchemaCopy.layouts.full[rowIndex].items = items

        // safety check — if nothing was found, exit quietly
        if (!removedItem) return

        // identify item type and key based on its prefix
        // '#' = field, '&' = list/subform
        const itemType = removedItem.type?.[0] === '&' ? 'list' : 'field'
        const itemKey = removedItem.type?.substring(1)

        // delete the backing data (must be after we remove references from layouts)
        if (itemType === 'field') {
            this.rememberRemovedField(itemKey, selectedSchemaCopy.fields?.[itemKey])
            delete selectedSchemaCopy.fields?.[itemKey]
        } else if (itemType === 'list' && this.schemaKey === 'root') {
            // only delete internal schema if we’re on the root
            delete schemaCopy.internalSchemas?.[itemKey]
        }

        // update schema observable
        this.schema = schemaCopy
    }

    deleteRow = (rowIndex: number) => {
        // this will delete the row AND any fields within it

        if (isNaN(rowIndex)) return

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const layoutFullCopy = (this.schemaKey === 'root') ? schemaCopy.layouts.full : schemaCopy.internalSchemas[this.schemaKey].layouts.full

        // grab row's items (before we delete the row)
        const fields = layoutFullCopy[rowIndex].items

        // delete the row
        layoutFullCopy.splice(rowIndex, 1)

        // delete the fields/items (after we delete the row)
        fields?.forEach(item => {
            const itemType = item.type?.[0] === '&' ? 'list' : 'field'
            const itemKey = item.type?.substring(1)
            if (itemType === 'field') {
                this.rememberRemovedField(itemKey, schemaCopy.fields[itemKey])
                delete schemaCopy.fields[itemKey]
            } else if (itemType === 'list' && this.schemaKey === 'root') {
                delete schemaCopy.internalSchemas[itemKey]
            }
        })

        // update schema ovservable
        this.schema = schemaCopy
    }

    // Only fields the tile actually references are worth keeping a title for, and only from the
    // root form, since a tile never offers subform fields
    private rememberRemovedField = (fieldId: string, field: Record<string, any>|undefined) => {
        if (this.schemaKey !== 'root') return
        if (!field?.title || !this.tileSchema?.fields?.includes(fieldId)) return

        this._removedFieldTitles = { ...this._removedFieldTitles, [fieldId]: field.title }
    }

    removeInternalSchema = (key: string) => {
        const schemaCopy = toJS(this.schema)
        if (!schemaCopy?.internalSchemas) return
        delete schemaCopy.internalSchemas[key]
        this.schema = schemaCopy
    }

    /**
     * Convert a legacy spacer (row-level or inline without field) to new format with field entry.
     * This enables editing of legacy spacers by creating the proper field structure.
     * @param rowIndex The index of the row containing the legacy spacer
     * @returns The new fieldId, or null if conversion failed
     */
    convertLegacySpacerToField = (rowIndex: number): string | null => {
        if (isNaN(rowIndex)) return null

        // copy object (remove from observability so we can manipulate)
        const schemaCopy = toJS(this.schema)
        const schema = (this.schemaKey === 'root') ? schemaCopy : schemaCopy.internalSchemas[this.schemaKey]
        const layoutFull = schema.layouts.full

        const row = layoutFull[rowIndex]
        if (!row) return null

        // Extract current spacer properties
        let height = 30
        let border = false

        if (row.type === 'spacer') {
            // Legacy row-level spacer: { type: 'spacer', height: 30, border: false }
            height = (row as any).height || 30
            border = (row as any).border || false
        } else {
            // Not a legacy spacer
            return null
        }

        // Generate new field ID
        const fieldId = `spacer-field-${this.UID}`

        // Create field entry
        schema.fields[fieldId] = {
            height,
            border,
            type: 'spacer',
            id: fieldId
        }

        // Update row to new format
        layoutFull[rowIndex] = {
            type: 'row',
            items: [{ type: `#${fieldId}` }]
        }

        // Update schema observable
        this.schema = schemaCopy

        return fieldId
    }

    deleteSchema = async () => {
        await this.api.Routes.DocumentSchema.delById(this.id)
        this.schemaStore.deleteById(this.id)
    }

    setName = (name: string) => {
        this.name = name
    }

    setDescription = (desc: string) => {
        this.description = desc
    }

    // an explicitly empty tile is not the same as never having configured one: revisions
    // snapshot this, and only null falls back to the live template's tile
    setTileSchema = (fieldIds: string[]) => {
        if (!fieldIds.length && !this.tileSchema) return

        this.tileSchema = { fields: fieldIds }
    }

    // iconMedia/iconFile default to null so a plain gallery pick clears any image —
    // a glyph icon and an uploaded image are mutually exclusive
    setIcon = ({icon, color, iconMedia = null, iconFile = null}: {
        icon: string
        color: string
        iconMedia?: APIDataMedia|null
        iconFile?: File|null
    }) => {
        this.icon = icon
        this.color = color
        this.iconMedia = iconMedia
        this.iconFile = iconFile
    }

    // uploads a newly picked icon file (if any), deleting the media it replaces;
    // otherwise passes through the current media unchanged
    private processIconMedia = async (schemaId: number): Promise<APIDataMedia|null> => {
        if (!this.iconFile) return this.iconMedia

        if (this.iconMedia?.id) {
            await this.api.Routes.DocumentSchema.delMediaById(schemaId, this.iconMedia.id)
        }

        // usage: 'icon' opts this upload into square, transparency-preserving
        // processing (sqr40/sqr110/sqr150/sqr500) sized for the small icon box
        return await this.schemaMediaUtils.uploadMedia(this.iconFile, schemaId, { usage: 'icon' }) ?? null
    }

    setIsEnabled = (enabled: boolean) => {
        this.isEnabled = enabled
    }

    setSchemaKey = (key: string) => {
        this.schemaKey = key
    }

    addCategory = (category: APIDataDocumentSchemaCategory) => {
        if (!category?.id) return
        if (this.categories.some(c => c.id === category.id)) return
        this.categories = [...this.categories, category]
    }

    removeCategory = (categoryId: number) => {
        this.categories = this.categories.filter(c => c.id !== categoryId)
    }

    setCategories = (categories: APIDataDocumentSchemaCategory[]) => {
        this.categories = categories
    }

    get defaultName() {
        return defaultName
    }

    get UID() {
        // unique number generator - no, not to NV Gaming Commission standards ;)
        return Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))
    }
}

decorate(injectable(), SchemaBuilderController)
decorate(inject(Api), SchemaBuilderController, 0)
decorate(inject(DocumentSchemaStore), SchemaBuilderController, 1)
decorate(inject(SchemaMediaUtils), SchemaBuilderController, 2)

export {
    SchemaBuilderController
}
